import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Title, 
  Group, 
  Text, 
  NumberInput, 
  Divider, 
  Button, 
  Select,
  Badge,
  SimpleGrid
} from '@mantine/core';
import { IconClock, IconRefresh } from '@tabler/icons-react';
import { useCacheConfig } from '../../contexts/cache-config-context';
import { EntityType } from '../../types/cache-types';

export function CacheConfigPanel() {
  const { config, updateConfig, resetConfig, isLoading, getCacheTime } = useCacheConfig();
  const [selectedEntity, setSelectedEntity] = useState<EntityType>('products');
  const [staleTime, setStaleTime] = useState(0);
  const [gcTime, setGcTime] = useState(0);
  
  // Aggiorna i valori mostrati quando cambia l'entità selezionata
  useEffect(() => {
    const cacheTime = getCacheTime(selectedEntity);
    setStaleTime(cacheTime / 60000); // converti da ms a minuti
    setGcTime(cacheTime / 60000);
  }, [selectedEntity, getCacheTime]);
  
  // Temporary admin check - in a real app this should come from auth context
  const isAdmin = true;
  
  const entityOptions = [
    { value: 'products', label: 'Prodotti' },
    { value: 'users', label: 'Utenti' },
    { value: 'settings', label: 'Impostazioni' },
    { value: 'requests', label: 'Richieste' },
    { value: 'request', label: 'Dettaglio Richieste' },
  ];
  
  const handleSave = () => {
    updateConfig({
      [selectedEntity]: staleTime * 60000 // converti minuti in ms
    });
  };
  
  return (
    <Card withBorder shadow="sm" p="md">
      <Group justify="space-between" mb="md">
        <Title order={4}>Configurazione Cache</Title>
        <Badge color="indigo">Admin</Badge>
      </Group>
      
      <Text size="sm" color="dimmed" mb="lg">
        Configura i tempi di cache per diversi tipi di dati. I tempi sono in minuti.
      </Text>
      
      <Select
        label="Tipo di dati"
        data={entityOptions}
        value={selectedEntity}
        onChange={(value) => setSelectedEntity(value as EntityType)}
        mb="md"
      />
      
      <SimpleGrid cols={2} mb="lg">
        <NumberInput
          label="Tempo di validità"
          description="Per quanto tempo i dati sono considerati aggiornati"
          value={staleTime}
          onChange={(value) => setStaleTime(Number(value) || 0)}
          min={1}
          max={120}
          leftSection={<IconClock size={16} />}
          suffix=" min"
        />
        
        <NumberInput
          label="Tempo di garbage collection"
          description="Per quanto tempo mantenere i dati inattivi in memoria"
          value={gcTime}
          onChange={(value) => setGcTime(Number(value) || 0)}
          min={1}
          max={240}
          leftSection={<IconClock size={16} />}
          suffix=" min"
        />
      </SimpleGrid>
      
      <Group justify="space-between" mt="xl">
        <Button 
          variant="outline"
          onClick={resetConfig}
        >
          Ripristina Default
        </Button>
        
        <Button onClick={handleSave}>
          Salva Configurazione
        </Button>
      </Group>
      
      <Divider my="lg" />
      
      <Title order={5} mb="xs">Configurazione Attuale</Title>
      
      <Text size="sm" component="span">
        Tempo di validità: <Badge color="blue">{staleTime} minuti</Badge>
      </Text>
      <Text size="sm" mt="xs" component="span">
        Tempo di GC: <Badge color="teal">{gcTime} minuti</Badge>
      </Text>
      
      <Text size="xs" color="dimmed" mt="md">
        Nota: Il tempo di validità determina quando i dati diventano obsoleti e devono essere ricaricati.
        Il tempo di garbage collection determina quanto a lungo i dati inattivi rimangono in memoria prima di essere eliminati.
      </Text>
    </Card>
  );
}